import { usePostHog } from "posthog-js/react";
import { useEffect, useRef } from "react";

import type { AppConfig } from "../../types";

import { getAnalyticsAttributes } from "./get-analytics-attributes";

export function usePostHogIdentify(appConfig: AppConfig) {
  const posthog = usePostHog();
  const lastConfigKey = useRef<string | null>(null);
  useEffect(() => {
    const analyticsAttributes = getAnalyticsAttributes(appConfig);

    if (!posthog || !analyticsAttributes.userId) {
      return;
    }
    const configKey = JSON.stringify(appConfig);
    if (lastConfigKey.current === configKey) {
      return;
    }
    posthog.register_once({ event_source: "app" });
    const distinctId = appConfig.userEmail;
    posthog.identify(distinctId, {
      email: appConfig.userEmail,
      name: appConfig.userName,
      customer_id: appConfig.customerId,
      customer_name: appConfig.customerName,
      customer_domain: appConfig.customerDomain,
      company_public_id: analyticsAttributes.companyPublicId ?? undefined,
      country_of_incorporation: appConfig.countryOfIncorporation ?? undefined,
      is_staff: !!analyticsAttributes.isStaff,
      is_test: !!analyticsAttributes.isTest,
      hubspot_contact_id: analyticsAttributes.hubspotContactId ?? undefined,
    });
    const aliasKey = `ph_aliased_${analyticsAttributes.userId}`;
    if (!localStorage.getItem(aliasKey)) {
      posthog.alias(analyticsAttributes.userId.toString(), distinctId);
      posthog.alias(analyticsAttributes.userUuid, distinctId);
      localStorage.setItem(aliasKey, "1");
    }
    if (analyticsAttributes.companyPublicId) {
      posthog.group("company", analyticsAttributes.companyPublicId, {
        name: appConfig.customerName,
        company_name: appConfig.customerName,
        company_domain: appConfig.customerDomain,
        company_public_id: analyticsAttributes.companyPublicId,
        country_of_incorporation: appConfig.countryOfIncorporation,
        industry: appConfig.industry ?? undefined,
        is_product_managed: analyticsAttributes.isProductManaged ?? undefined,
      });
    }
    lastConfigKey.current = configKey;
  }, [appConfig, posthog]);
}
